'use es6';

import { useMemo } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useRelevantCollections } from 'marketplace-ui-common/collections/hooks/useRelevantCollections';
import AssetBusinessTypeUrls from '../constants/urls/AssetBusinessType';
import AssetPageTypeUrls from '../constants/urls/AssetPageType';
import AssetPageFeatureUrls from '../constants/urls/AssetPageFeature';
import ThemesAllBusinessTypesContainer from '../containers/ThemesAllBusinessTypesContainer';
import StorefrontUsageTrackerProvider from '../context/StorefrontUsageTrackerProvider';
import { getIsInApp } from '../selectors/app';
import AssetStorefrontContainer from '../storefront/containers/AssetStorefrontContainer';
import ThemeCollectionContainer from '../themes/containers/ThemeCollectionContainer';
import ThemeDetailsContainer from '../themes/containers/ThemeDetailsContainer';
import ThemeDetailsPreviewContainer from '../themes/containers/ThemeDetailsPreviewContainer';
import { getPaginatedRoute } from '../utils/pagination';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const makeObjectValuesRouteParam = (obj, name) => {
  const routeValues = Object.values(obj).join('|');
  return `:${name}(${routeValues})`;
};
export const businessTypeRouteParam = makeObjectValuesRouteParam(AssetBusinessTypeUrls, 'businessType');
export const pageTypeRouteParam = makeObjectValuesRouteParam(AssetPageTypeUrls, 'pageType');
export const pageFeatureRouteParam = makeObjectValuesRouteParam(AssetPageFeatureUrls, 'pageFeature');
export default function ThemesRoutes() {
  const {
    path
  } = useRouteMatch();
  const isInApp = useSelector(getIsInApp);
  const {
    data: collections,
    error,
    loading
  } = useRelevantCollections({
    appContext: isInApp ? 'app' : 'public',
    offeringType: 'THEME'
  });
  const collectionRoutes = useMemo(() => {
    const collectionRouteParam = `:collection(${collections.map(({
      slug
    }) => slug).join('|')})`;
    return [`${path}/${collectionRouteParam}`, getPaginatedRoute(`${path}/${collectionRouteParam}`)];
  }, [collections, path]);
  const homeRoute = path;
  const homePageRoute = getPaginatedRoute(homeRoute);
  const homeRoutes = [homeRoute, homePageRoute];
  const categoryRoutes = useMemo(() => [`${path}/${businessTypeRouteParam}`, getPaginatedRoute(`${path}/${businessTypeRouteParam}`), `${path}/${pageTypeRouteParam}`, getPaginatedRoute(`${path}/${pageTypeRouteParam}`), `${path}/${pageFeatureRouteParam}`, getPaginatedRoute(`${path}/${pageFeatureRouteParam}`), `${path}/${businessTypeRouteParam}/${pageTypeRouteParam}`, getPaginatedRoute(`${path}/${businessTypeRouteParam}/${pageTypeRouteParam}`), `${path}/${businessTypeRouteParam}/${pageFeatureRouteParam}`, getPaginatedRoute(`${path}/${businessTypeRouteParam}/${pageFeatureRouteParam}`), `${path}/${pageTypeRouteParam}/${pageFeatureRouteParam}`, getPaginatedRoute(`${path}/${pageTypeRouteParam}/${pageFeatureRouteParam}`)], [path]);
  if (loading || error) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Switch, {
    children: [/*#__PURE__*/_jsx(Route, {
      path: `${path}/themes/_preview/:offeringId`,
      exact: true,
      sensitive: true,
      children: /*#__PURE__*/_jsx(ThemeDetailsPreviewContainer, {})
    }), /*#__PURE__*/_jsx(Route, {
      children: /*#__PURE__*/_jsxs(Switch, {
        children: [/*#__PURE__*/_jsx(Route, {
          path: [`${path}/categories`, ...homeRoutes, ...collectionRoutes, ...categoryRoutes],
          exact: true,
          sensitive: true,
          children: /*#__PURE__*/_jsx(StorefrontUsageTrackerProvider, {
            children: /*#__PURE__*/_jsxs(Switch, {
              children: [/*#__PURE__*/_jsx(Route, {
                path: homeRoutes,
                exact: true,
                children: /*#__PURE__*/_jsx(AssetStorefrontContainer, {})
              }), /*#__PURE__*/_jsx(Route, {
                path: collectionRoutes,
                exact: true,
                component: ThemeCollectionContainer
              }), /*#__PURE__*/_jsx(Route, {
                path: `${path}/categories`,
                exact: true,
                children: /*#__PURE__*/_jsx(ThemesAllBusinessTypesContainer, {})
              }), /*#__PURE__*/_jsx(Route, {
                path: categoryRoutes,
                exact: true,
                children: /*#__PURE__*/_jsx(AssetStorefrontContainer, {})
              }), /*#__PURE__*/_jsx(Redirect, {
                to: path
              })]
            })
          })
        }), /*#__PURE__*/_jsx(Route, {
          path: [`${path}/all`, getPaginatedRoute(`${path}/all`)],
          render: routeProps => {
            const {
              match: {
                params: {
                  pageNumber
                }
              },
              location: {
                search
              }
            } = routeProps;
            return /*#__PURE__*/_jsx(Redirect, {
              to: {
                pathname: `${path}${pageNumber ? `/page/${pageNumber}` : ''}`,
                search
              }
            });
          },
          exact: true,
          sensitive: true
        }), /*#__PURE__*/_jsx(Route, {
          path: [`${path}/themes`, getPaginatedRoute(`${path}/themes`)],
          render: routeProps => {
            const {
              match: {
                params: {
                  pageNumber
                }
              },
              location: {
                search
              }
            } = routeProps;
            return /*#__PURE__*/_jsx(Switch, {
              children: /*#__PURE__*/_jsx(Redirect, {
                to: {
                  pathname: `${path}/all${pageNumber ? `/page/${pageNumber}` : ''}`,
                  search
                }
              })
            });
          },
          exact: true,
          sensitive: true
        }), /*#__PURE__*/_jsx(Redirect, {
          from: `${path}/themes/all-business-types`,
          to: `${path}/categories`,
          exact: true,
          sensitive: true
        }), /*#__PURE__*/_jsx(Redirect, {
          from: `${path}/themes/:slug`,
          to: `${path}/:slug`,
          exact: true,
          sensitive: true
        }), /*#__PURE__*/_jsx(Redirect, {
          from: `${path}/themes/:slug/write-review`,
          to: `${path}/:slug/write-review`,
          exact: true,
          sensitive: true
        }), /*#__PURE__*/_jsx(Route, {
          path: [`${path}/:slug`, `${path}/:slug/write-review`],
          exact: true,
          sensitive: true,
          children: /*#__PURE__*/_jsx(ThemeDetailsContainer, {})
        }), /*#__PURE__*/_jsx(Redirect, {
          to: path
        })]
      })
    }), /*#__PURE__*/_jsx(Redirect, {
      to: path
    })]
  });
}